import {computed, effect, inject, Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {DefaultValuesService} from '../default-values.service';
import {EnumsService} from '../enums.service';
import {ToolsService} from '../tools.service';
import {UrlBuilderService} from '../url-builder.service';
import {RoutesEnums} from '../../enums/RoutesEnums';
import {
  SearchLabelEnum,
  SearchPlaceLabelEnum,
  SearchPlaceTypeNameEnum,
  SearchStateFieldsEnum,
  SearchSuggestTypesEnum,
} from '../../enums/SearchEnums';
import {QueryParams} from '../../types/QueryParams';
import {SearchState} from '../../types/SearchState';
import {SearchSuggestItem} from '../../types/SearchSuggestItem';
import {SearchWidgetDataItem} from '../../types/SearchWidgetDataItem';
import {SearchWidgetState} from '../../components/search-widget-v2/typings/search-widget-state';
import {NoScrollService} from '../no-scroll.service';
import {DataLayerParamsClickGA4} from '../../types/DataLayerParamsClickGA4';
import {SearchResultContent} from '../../types/SearchResultContent';
import {GtmEventEnum} from '../../enums/GoogleEnums';
import {GtmGa4EventPushService} from '../gtm-ga4-event-push.service';
import {SearchWidgetUrlBuilderService} from './search-widget-url-builder.service';
import {SearchWidgetLabelsEnum} from '../../components/search-widget-v2/enums/search-widget-labels.enum';
import {SearchSuggestItemData} from '../../types/SearchSuggestItemData';
import {SearchService} from '../search/search.service';
import {UserLocationState} from '../../types/UserLocationState';
import {SearchReloadService} from '../search/search-reload.service';
import {isPlatformBrowser} from '@angular/common';
import {DLPageTypeEnum} from '../../enums/DataLayerEnums';

@Injectable({
  providedIn: 'root',
})
export class SearchWidgetManagerService implements OnDestroy {
  private subscriptions: Subscription[] = [];

  private baseUrl: string[] = ['/', RoutesEnums.SEARCH];
  private stateUrlPatchField: string = 'placeUrlPath';

  private effectsTriggerMonitor = {
    mobilePlacesPanelOpen: null,
    mobileCategoriesPanelOpen: null,
    isMobileWidgetExpanded: null,
    triggerLocationSearch: null,
    searchResultContent: null,
    widgetData: null,
  };

  private searchService = inject(SearchService);
  private triggerLocationSearch = this.searchService.triggerLocationSearch;
  private placePanelOpen = this.searchService.placePanelOpen;
  private mobilePlacesPanelOpen = this.searchService.mobilePlacesPanelOpen;
  private mobileCategoriesPanelOpen = this.searchService.mobileCategoriesPanelOpen;
  private isMobileWidgetExpanded = this.searchService.isMobileWidgetExpanded;
  private mobileFiltersPanelOpen = this.searchService.mobileFiltersPanelOpen;
  private megaMenuDesktopOpen = this.searchService.megaMenuDesktopOpen;
  private megaMenuMobileOpen = this.searchService.megaMenuMobileOpen;
  private searchWidgetData = this.searchService.searchWidgetData;
  private state = this.searchService.searchWidgetState;
  private widgetData = computed(() => (this.searchWidgetData() ? this.searchWidgetData() : null));
  private searchState = this.searchService.searchState;
  public canRunSearch = computed(() => {
    const defaultSearchState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    return (
      this.searchState().freeSearchTerm !== defaultSearchState.freeSearchTerm ||
      this.searchState().caregiverUnitTypeUrlPath !== defaultSearchState.caregiverUnitTypeUrlPath ||
      this.searchState().careServiceTypeUrlPath !== defaultSearchState.careServiceTypeUrlPath ||
      this.searchState().symptomUrlPath !== defaultSearchState.symptomUrlPath ||
      this.searchState().placeUrlPath !== defaultSearchState.placeUrlPath ||
      this.state().autocompletePlaceCurrentValue !== '' ||
      this.state().autocompleteCurrentValue !== ''
    );
  });
  private searchResults = this.searchService.searchResults;
  private searchResultContent = computed<SearchResultContent>(() => this.searchResults()?.searchResultContent);

  constructor(
    private searchWidgetUrlBuilderService: SearchWidgetUrlBuilderService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private enumsService: EnumsService,
    private defaults: DefaultValuesService,
    private router: Router,
    private noScroll: NoScrollService,
    private gtmEventPush: GtmGa4EventPushService,
    private reloadService: SearchReloadService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.setListeners();
    effect(
      () => {
        this.noScroll.setScrollBlocked(
          this.mobileFiltersPanelOpen() ||
            this.mobileCategoriesPanelOpen() ||
            this.mobilePlacesPanelOpen() ||
            this.placePanelOpen() ||
            this.megaMenuDesktopOpen() ||
            this.megaMenuMobileOpen() ||
            this.isMobileWidgetExpanded(),
        );
      },
      {
        allowSignalWrites: true,
      },
    );
    effect(
      () => {
        if (this.effectsTriggerMonitor.isMobileWidgetExpanded !== this.isMobileWidgetExpanded()) {
          this.updateValuesInSearchWidgetState(this.isMobileWidgetExpanded());
          this.effectsTriggerMonitor.isMobileWidgetExpanded = this.tools.clone(this.isMobileWidgetExpanded());
        }
      },
      {
        allowSignalWrites: true,
      },
    );
    effect(
      () => {
        if (this.effectsTriggerMonitor.triggerLocationSearch !== this.triggerLocationSearch()) {
          this.handleTriggerLocationSearch(this.triggerLocationSearch());
          this.effectsTriggerMonitor.triggerLocationSearch = this.tools.clone(this.triggerLocationSearch());
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        if (this.widgetData() && !this.tools.isEqual(this.effectsTriggerMonitor.widgetData, this.widgetData())) {
          this.updateValuesInSearchWidgetState();
          this.effectsTriggerMonitor.widgetData = this.tools.clone(this.widgetData());
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        if (this.searchState() && this.searchState().locationSearch === true) {
          this.getUserLocation(null, false, false, true);
          this.updateValuesInSearchWidgetState();
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    effect(
      () => {
        if (
          this.searchResultContent() &&
          !this.tools.isEqual(this.effectsTriggerMonitor.searchResultContent, this.searchResultContent())
        ) {
          this.updateValuesInSearchWidgetState();
          this.effectsTriggerMonitor.searchResultContent = this.tools.clone(this.searchResultContent());
        }
      },
      {
        allowSignalWrites: true,
      },
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public getUserLocation(
    callback: Function,
    proceedWithRedirect = false,
    onlySweedishArea: boolean,
    setLocationSearchMode: boolean,
  ): void {
    if (isPlatformBrowser(this.platformId)) {
      if (navigator.geolocation) {
        const options: PositionOptions = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 60000,
        };
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.searchService.isLocationLoading.set(false);
            if (
              (onlySweedishArea === true && this.isGeolocationInSwedenArea(position) === true) ||
              onlySweedishArea !== true
            ) {
              this.handleLocation(
                position,
                () => {
                  if (proceedWithRedirect) {
                    const queryParams: QueryParams = this.searchWidgetUrlBuilderService.getSearchResultsQueryParams(
                      null,
                      this.stateUrlPatchField,
                      true,
                      false,
                      false,
                      this.searchState(),
                      this.state(),
                    );

                    const routerLink: string[] = this.searchWidgetUrlBuilderService.getSearchResultsRouterLink(
                      null,
                      this.stateUrlPatchField,
                      true,
                      false,
                      this.searchState(),
                      this.state(),
                    );

                    this.router.navigate(routerLink, {queryParams});
                  }
                  if (callback != null) {
                    callback();
                  }
                },
                setLocationSearchMode,
              );
            } else {
              if (callback != null) {
                callback();
              }
            }
          },
          (error) => this.handleLocationError(error),
          options,
        );
      } else {
        this.handleLocationError(null);
      }
    }
  }

  public freeSearchEnterHit(freeSearchTerm: string, placesTerm: string): void {
    if (freeSearchTerm !== '') {
      this.state().autocompleteCurrentValue = freeSearchTerm;
    }
    if (placesTerm !== '') {
      this.state().autocompletePlaceCurrentValue = placesTerm;
      this.state().placeSearchValue = '';
    }
    this.searchAction();
  }

  public searchAction(): void {
    let searchState: SearchState = null;
    if (
      this.state().autocompleteCurrentValue !== '' ||
      this.state().placeSearchValue ||
      this.state().autocompletePlaceCurrentValue
    ) {
      const currentSearchState: SearchState = this.tools.clone(this.searchState());
      searchState = this.tools.clone(this.defaults.getDefaultSearchState());
      if (!!this.state().autocompletePlaceCurrentValue) {
        searchState.freeSearchTerm = `${this.state().autocompleteCurrentValue} ${
          this.state().autocompletePlaceCurrentValue
        }`.trim();
        searchState.placeUrlPath = '';
      } else {
        searchState.freeSearchTerm = this.state().autocompleteCurrentValue;
        searchState.placeUrlPath = this.state().placeSearchValue;
      }
      if (this.state().placeSearchValueLabel === SearchWidgetLabelsEnum.NARA_MIG) {
        searchState.locationSearch = currentSearchState.locationSearch;
        searchState.lat = currentSearchState.lat;
        searchState.lon = currentSearchState.lon;
      }
    } else {
      const currentSearchState: SearchState = this.tools.clone(this.searchState());
      searchState = this.tools.clone(this.defaults.getDefaultSearchState());
      searchState.locationSearch = currentSearchState.locationSearch;
      searchState.placeUrlPath = currentSearchState.placeUrlPath;
      searchState.symptomUrlPath = currentSearchState.symptomUrlPath;
      searchState.careServiceTypeUrlPath = currentSearchState.careServiceTypeUrlPath;
      searchState.caregiverUnitTypeUrlPath = currentSearchState.caregiverUnitTypeUrlPath;
      searchState.freeSearchTerm = currentSearchState.freeSearchTerm;
      searchState.lat = currentSearchState.lat;
      searchState.lon = currentSearchState.lon;
    }

    const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(searchState, this.baseUrl);
    const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(searchState);
    this.reloadService.reload(routerLink, queryParams);
    this.router.navigate(routerLink, {queryParams});
  }

  public redirectToSearchResultsForSelectedSuggestItem(selectedObject: SearchSuggestItem) {
    const currentSearchState: SearchState = this.tools.clone(this.searchState());
    let searchState = this.tools.clone(this.defaults.getDefaultSearchState());
    searchState.locationSearch = currentSearchState.locationSearch;
    searchState.placeUrlPath = currentSearchState.placeUrlPath;
    if (searchState.locationSearch === true) {
      searchState.lat = currentSearchState.lat;
      searchState.lon = currentSearchState.lon;
    }

    let navigateToSearchResults = true;
    searchState.freeSearchTerm = '';
    searchState.symptomUrlPath = '';
    searchState.careServiceTypeUrlPath = '';
    searchState.caregiverUnitTypeUrlPath = '';
    if (this.tools.isNil(selectedObject.searchValue.find((item) => item.type === SearchSuggestTypesEnum.PLACE))) {
      searchState = this.updateSearchStateWithPlaceCurrentValues(searchState);
    }

    selectedObject.searchValue.forEach((item) => {
      if (item.type === SearchSuggestTypesEnum.CAREPROVIDER_UNIT) {
        navigateToSearchResults = false;
        this.redirectToCareproviderUnitPageWhenSelected(item.urlPath);
      } else if (item.type === SearchSuggestTypesEnum.CAREPROVIDER) {
        navigateToSearchResults = false;
        this.redirectToCareproviderLandingPageWhenSelected(item.urlPath);
      } else {
        const enumKey: string = this.enumsService.getEnumKeyByEnumValue(SearchSuggestTypesEnum, item.type);
        searchState[SearchStateFieldsEnum[enumKey]] = item.urlPath;
      }
    });

    if (navigateToSearchResults) {
      const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(searchState, this.baseUrl);
      const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(searchState);
      this.reloadService.reload(routerLink, queryParams);
      this.router.navigate(routerLink, {queryParams});
    }
  }

  public clearFreeSearchTerm(excludePlaceFromFreeSearchTerm: boolean): void {
    const defaultSearchState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    const newSearchState = this.tools.clone(this.searchState());
    newSearchState.caregiverUnitTypeUrlPath = defaultSearchState.caregiverUnitTypeUrlPath;
    newSearchState.careServiceTypeUrlPath = defaultSearchState.careServiceTypeUrlPath;
    newSearchState.symptomUrlPath = defaultSearchState.symptomUrlPath;
    if (excludePlaceFromFreeSearchTerm === false) {
      newSearchState.placeUrlPath = defaultSearchState.placeUrlPath;
    }
    newSearchState.freeSearchTerm = defaultSearchState.freeSearchTerm;
    newSearchState.page = defaultSearchState.page;
    newSearchState.sort = defaultSearchState.sort;
    newSearchState.holdResultsReload = true;

    const state: SearchWidgetState = this.tools.clone(this.state());
    state.autocompleteCurrentValue = '';
    state.autocompleteValueChanged = true;
    if (excludePlaceFromFreeSearchTerm === false) {
      state.placeSearchValue = '';
      state.placeSearchValueLabel = '';
    }
    state.isLocationSearchVisible =
      newSearchState.careServiceTypeUrlPath !== '' ||
      newSearchState.caregiverUnitTypeUrlPath !== '' ||
      newSearchState.symptomUrlPath !== '' ||
      newSearchState.freeSearchTerm !== '' ||
      newSearchState.locationSearch === true;

    // this.searchService.updateSearchState(newSearchState);
    // this.searchService.updateSearchWidgetState(state);
    //redirect to search results
    const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(newSearchState, this.baseUrl);
    const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(newSearchState);
    this.reloadService.reload(routerLink, queryParams);
    this.router.navigate(routerLink, {queryParams});
  }

  clearPlacesFreeSearchTerm(): void {
    const defaultSearchState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    const newSearchState = this.tools.clone(this.searchState());
    newSearchState.placeUrlPath = defaultSearchState.placeUrlPath;
    newSearchState.holdResultsReload = true;

    const state: SearchWidgetState = this.tools.clone(this.state());
    state.autocompletePlaceCurrentValue = '';
    state.autocompletePlaceValueChanged = true;
    state.placeSearchValue = '';
    state.placeSearchValueLabel = '';

    // this.searchService.updateSearchState(newSearchState);
    // this.searchService.updateSearchWidgetState(state);
    //redirect to search results

    const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(newSearchState, this.baseUrl);
    const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(newSearchState);
    this.reloadService.reload(routerLink, queryParams);
    this.router.navigate(routerLink, {queryParams});
  }

  public updateStateAndSetFocusOnFreeSearchField(state: SearchState): void {
    this.searchService.updateSearchState(state);
    const newState = this.tools.clone(this.state());
    newState.freeSearchFieldFocus = true;
    newState.placesFieldFocus = false;
    this.searchService.updateSearchWidgetState(newState);
  }

  public setFocusOnFreeSearchField(): void {
    const newState = this.tools.clone(this.state());
    newState.freeSearchFieldFocus = true;
    newState.placesFieldFocus = false;
    this.searchService.updateSearchWidgetState(newState);
  }

  public setFocusOnPlacesField(): void {
    const newState = this.tools.clone(this.state());
    newState.freeSearchFieldFocus = false;
    newState.placesFieldFocus = true;
    this.searchService.updateSearchWidgetState(newState);
  }

  public setFocusOffPlacesField(): void {
    const newState = this.tools.clone(this.state());
    newState.placesFieldFocus = false;
    this.searchService.updateSearchWidgetState(newState);
  }

  public setFixedWidgetPosition(fixed: boolean): void {
    this.searchService.fixedWidget.set(fixed);
  }

  public softResetWidgetState(): void {
    this.searchService.resetWidgetState();
  }

  public onPlacesSelection(placeName: string, freeSearchValue: string): void {
    const searchState = this.tools.clone(this.defaults.getDefaultSearchState());
    searchState.freeSearchTerm = [freeSearchValue, placeName].join(' ').replace(/\s+/g, ' ');

    const routerLink: string[] = this.urlBuilder.CreateSearchResultsRouterLink(searchState, this.baseUrl);
    const queryParams: QueryParams = this.urlBuilder.CreateSearchResultsQueryParams(searchState);
    this.reloadService.reload(routerLink, queryParams);
    this.router.navigate(routerLink, {queryParams});
  }

  public getSearchStateUpdatedWithCurrentWidgetState(searchState: SearchState): SearchState {
    if (this.state().autocompletePlaceValueChanged) {
      searchState.placeUrlPath = this.state().placeSearchValue;
    }
    return searchState;
  }

  public getDataLayerParamsClickGA4FromSearchResultContent(
    cta: string,
    searchResultContent: SearchResultContent,
    pageType: DLPageTypeEnum,
  ): DataLayerParamsClickGA4 {
    const customGa4Params: DataLayerParamsClickGA4 = {
      callToAction: cta,
      pageType: pageType,
    };
    if (searchResultContent) {
      if (searchResultContent.caregiverUnitType) {
        customGa4Params.inferredCategoryTypeId = searchResultContent.caregiverUnitType.id;
      }
      if (searchResultContent.caregiverUnitType && searchResultContent.caregiverUnitType.area) {
        customGa4Params.inferredCareProviderTypeAreaId = searchResultContent.caregiverUnitType.area.id;
      }
      if (searchResultContent.careServiceType) {
        customGa4Params.careServiceTypeId = searchResultContent.careServiceType.id;
      }
      if (searchResultContent.symptom) {
        customGa4Params.symptomId = searchResultContent.symptom.id;
      }
    }
    return customGa4Params;
  }

  public registerGtmGa4Event(
    cta: string,
    eventType: GtmEventEnum,
    pageType: DLPageTypeEnum,
    mainCareProviderTypeId?: number,
    inferredCareProviderTypeAreaId?: number,
    inferredCategoryTypeId?: number,
  ): void {
    const customGa4Params: DataLayerParamsClickGA4 = this.getDataLayerParamsClickGA4FromSearchResultContent(
      cta,
      this.searchResultContent(),
      pageType,
    );
    if (mainCareProviderTypeId && mainCareProviderTypeId > 0) {
      customGa4Params.mainCareProviderTypeId = mainCareProviderTypeId;
    }
    if (inferredCareProviderTypeAreaId && inferredCareProviderTypeAreaId > 0) {
      customGa4Params.inferredCareProviderTypeAreaId = inferredCareProviderTypeAreaId;
    }
    if (inferredCategoryTypeId && inferredCategoryTypeId > 0) {
      customGa4Params.inferredCategoryTypeId = inferredCategoryTypeId;
    }

    const gtmGa4Tag = {
      event: eventType,
      ...customGa4Params,
    };
    this.gtmEventPush.pushClickEvent(gtmGa4Tag);
  }

  public updateValuesInSearchWidgetState(isMobileWidgetExpanded?: boolean): void {
    this.updateFreeSearchTermInSearchWidgetState();
    this.updatePlaceNameInSearchWidgetState();
  }

  public loadPlaces(): void {
    this.searchService.reloadPlaces();
  }

  public getSuggestTypeLabel(suggestItem: SearchSuggestItem): string {
    let typeLabel = '';
    if (suggestItem.searchValue.length === 1) {
      const type: string = suggestItem.searchValue[0].type;
      const enumKey: string = this.enumsService.getEnumKeyByEnumValue(SearchSuggestTypesEnum, type);
      if (enumKey == 'PLACE' && suggestItem.searchValue[0]) {
        typeLabel = this.getSuggestTypeLabelForPlace(suggestItem.searchValue[0].data);
      } else {
        typeLabel = SearchLabelEnum[enumKey];
      }
    }
    return typeLabel;
  }

  private getSuggestTypeLabelForPlace(data: string): string {
    const dataOb: SearchSuggestItemData = JSON.parse(data);
    switch (dataOb.TypeName) {
      case SearchPlaceTypeNameEnum.COUNTY:
        return SearchPlaceLabelEnum.COUNTY;
      case SearchPlaceTypeNameEnum.MUNICIPALITY:
        return SearchPlaceLabelEnum.MUNICIPALITY;
      case SearchPlaceTypeNameEnum.DISTRICT:
        return SearchPlaceLabelEnum.DISTRICT;
      default:
        return SearchPlaceLabelEnum.DEFAULT;
    }
  }

  private updateSearchStateWithPlaceCurrentValues(searchState: SearchState): SearchState {
    if (this.state().autocompletePlaceValueChanged) {
      if (this.state().autocompletePlaceCurrentValue !== this.state().placeSearchValue) {
        searchState.freeSearchTerm = this.state().autocompletePlaceCurrentValue;
        searchState.placeUrlPath = '';
      } else if (this.state().placeSearchValue !== '') {
        searchState.placeUrlPath = this.state().placeSearchValue;
      } else {
        searchState.freeSearchTerm = this.state().autocompletePlaceCurrentValue;
        searchState.placeUrlPath = '';
      }
    }
    return searchState;
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => this.handleRouteChange(event)),
    );
  }

  private handleRouteChange(event: NavigationEnd): void {
    this.searchService.isMobileWidgetExpanded.set(false);
  }

  private updatePlaceNameInSearchWidgetState(): void {
    if (this.searchState() && this.searchState().holdResultsReload === true) {
      return;
    }

    const defaultState: SearchWidgetState = this.tools.clone(this.defaults.getDefaultSearchWidgetState());
    if (
      this.searchResultContent() &&
      this.searchResultContent().place &&
      this.searchResultContent().allSolved === false
    ) {
      const state = this.tools.clone(this.state());
      state.placeSearchValue = this.searchResultContent().place.urlPath;
      state.placeSearchValueLabel = this.searchResultContent().place.name;
      state.autocompletePlaceCurrentValue = this.searchResultContent().place.name;
      this.searchService.updateSearchWidgetState(state);
    } else if (this.searchResultContent() === null) {
      const state = this.tools.clone(this.state());
      state.placeSearchValue = defaultState.placeSearchValue;
      state.placeSearchValueLabel = defaultState.placeSearchValueLabel;
      state.autocompletePlaceCurrentValue = defaultState.autocompletePlaceCurrentValue;
      state.autocompletePlaceValueChanged = false;
      this.searchService.updateSearchWidgetState(state);
    }
  }

  private updateFreeSearchTermInSearchWidgetState(): void {
    if (!this.searchState() || !this.widgetData()) {
      return;
    }

    if (this.searchState() && this.searchState().holdResultsReload === true) {
      return;
    }

    let termParts: string[] = [];
    termParts = this.addFreeSearchTermToTermPartsIfSelected(termParts);
    termParts = this.addCpruTypeToTermPartsIfSelected(termParts);
    termParts = this.addServiceTypeToTermPartsIfSelected(termParts);
    termParts = this.addSymptomToTermPartsIfSelected(termParts);
    // place is no longer merged with free search VS-6708
    // termParts =
    //   this.isMobile && !this.isMobileWidgetExpanded ? this.addPlaceToTermPartsIfSelected(termParts) : termParts;

    const state = this.tools.clone(this.state());
    state.autocompleteCurrentValue = termParts.join(', ');
    state.autocompleteValueChanged = false;
    this.searchService.updateSearchWidgetState(state);
  }

  private addFreeSearchTermToTermPartsIfSelected(termParts: string[]): string[] {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    if (
      this.searchResultContent() &&
      this.searchResultContent().freeText !== null &&
      this.searchResultContent().freeText !== '' &&
      this.searchResultContent().allSolved === false
    ) {
      termParts.push(this.searchResultContent().freeText);
    } else if (this.searchState().freeSearchTerm !== defaultState.freeSearchTerm) {
      termParts.push(this.searchState().freeSearchTerm);
    }
    return termParts;
  }

  private addCpruTypeToTermPartsIfSelected(termParts: string[]): string[] {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    if (this.searchState().caregiverUnitTypeUrlPath !== defaultState.caregiverUnitTypeUrlPath) {
      const cpruTypeSearchItem: SearchWidgetDataItem = this.searchService.getWidgetDataItemByFieldValue(
        this.widgetData().caregiverUnitTypes,
        this.searchState().caregiverUnitTypeUrlPath,
        'urlPath',
      );
      if (!this.tools.isNil(cpruTypeSearchItem)) {
        termParts.push(cpruTypeSearchItem.name);
      }
    }
    return termParts;
  }

  private addServiceTypeToTermPartsIfSelected(termParts: string[]): string[] {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    if (this.searchState().careServiceTypeUrlPath !== defaultState.careServiceTypeUrlPath) {
      const serviceTypeSearchItem: SearchWidgetDataItem = this.searchService.getWidgetDataItemByFieldValue(
        this.widgetData().careServiceTypes,
        this.searchState().careServiceTypeUrlPath,
        'urlPath',
      );
      if (!this.tools.isNil(serviceTypeSearchItem)) {
        termParts.push(serviceTypeSearchItem.name);
      }
    }
    return termParts;
  }

  private addSymptomToTermPartsIfSelected(termParts: string[]): string[] {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    if (this.searchState().symptomUrlPath !== defaultState.symptomUrlPath) {
      const symptomSearchItem: SearchWidgetDataItem = this.searchService.getWidgetDataItemByFieldValue(
        this.widgetData().symptoms,
        this.searchState().symptomUrlPath,
        'urlPath',
      );
      if (!this.tools.isNil(symptomSearchItem)) {
        termParts.push(symptomSearchItem.name);
      }
    }
    return termParts;
  }

  private addPlaceToTermPartsIfSelected(termParts: string[]): string[] {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    if (this.searchResultContent() && this.searchResultContent().allSolved === false) {
      if (this.searchResultContent().place) {
        termParts.push(this.searchResultContent().place.name);
      }
    }
    if (this.state().isLocationSearchActive) {
      termParts.push(SearchWidgetLabelsEnum.NARA_MIG);
    }
    return termParts;
  }

  private handleLocation(position: GeolocationPosition, callback: Function, setLocationSearchMode: boolean): void {
    const userLocationState: UserLocationState = {
      locationSearch: setLocationSearchMode,
      lat: position.coords.latitude,
      lon: position.coords.longitude,
    };
    this.searchService.updateUserLocationState(userLocationState);
    const state = this.tools.clone(this.state());
    state.askedAboutLocation = true;
    if (setLocationSearchMode === true) {
      state.placeSearchValueLabel = SearchWidgetLabelsEnum.NARA_MIG;
      state.autocompletePlaceCurrentValue = '';
      state.autocompletePlaceValueChanged = false;
      state.placeSearchValue = '';
    }

    const newSearchState: SearchState = this.tools.clone(this.searchState());
    newSearchState.locationSearch = setLocationSearchMode;
    newSearchState.lat = position.coords.latitude;
    newSearchState.lon = position.coords.longitude;
    this.searchService.updateSearchState(newSearchState);
    this.searchService.updateSearchWidgetState(state);

    if (callback != null) {
      callback();
    }
  }

  private handleLocationError(error: GeolocationPositionError): void {
    let errorMsg = '';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMsg = 'User denied the request for Geolocation.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMsg = 'SimpleLocation information is unavailable.';
        break;
      case error.TIMEOUT:
        errorMsg = 'The request to get user location timed out.';
        break;
      default:
        errorMsg = 'Geolocation is not supported by this browser.';
        break;
    }
    const state = this.tools.clone(this.state());
    state.askedAboutLocation = true;

    const userLocationState: UserLocationState = {
      locationSearch: false,
      lat: 0,
      lon: 0,
    };

    this.searchService.updateSearchWidgetState(state);
    this.searchService.updateUserLocationState(userLocationState);
    this.searchService.isLocationLoading.set(false);
    console.error(errorMsg);
  }

  private redirectToCareproviderUnitPageWhenSelected(urlPath: string): void {
    const routerLink: string[] = this.urlBuilder.CreateCareproviderUnitRouterLinkFromString(urlPath);
    this.router.navigate(routerLink, {});
  }

  private redirectToCareproviderLandingPageWhenSelected(urlPath: string): void {
    const routerLink: string[] = this.urlBuilder.CreateCareproviderLandingPageRouterLinkFromString(urlPath);
    this.router.navigate(routerLink, {});
  }

  private isGeolocationInSwedenArea(position: GeolocationPosition): boolean {
    return this.tools.isLocationInSwedenArea(position.coords.latitude, position.coords.longitude);
  }

  private handleTriggerLocationSearch(result: boolean): void {
    if (result === true) {
      const callback = () => {
        this.searchService.triggerLocationSearch.set(false);
      };
      this.getUserLocation(callback, true, false, true);
    }
  }
}
